import React, { useState, CSSProperties, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useGetSettlementsQuery } from '../../../../../../../../redux/services/SettlementsApi';
import { Table } from '../../../../../../../../components/Table/Table';
import { Pagination } from '../../../../../../../../components/Pagination/Pagination';
import { ModalDefault } from '../../../../../../../../components/ModalDefault/ModalDefault';
import { TSettlementModel } from '../../../../../../../../redux/types/SettlementsTypes';
import { FilterValues } from '../../../CustomFilterModal/CustomFilterModal';

export interface thModel {
  name: string;
  style?: CSSProperties;
}

interface Td { element: React.ReactNode; style: CSSProperties | undefined }

const settlementsThArray: thModel[] = [
  { name: 'ID', style: { width: '15%' } },
  { name: 'Дата создания', style: { width: '15%' } },
  { name: 'Направление', style: { width: '15%' } },
  { name: 'Кошелек', style: { width: '15%' } },
  { name: 'Валюта', style: { width: '15%' } },
  { name: 'Сумма', style: { width: '15%' } },
  { name: 'Статус', style: { width: '15%' } },
];

const linkStyle: CSSProperties = {
  cursor: 'pointer',
  color: 'blue',
  textDecoration: 'underline',
  wordBreak: 'break-all',
  overflowWrap: 'anywhere',
};

interface Props {
  filter?: FilterValues | null;
}

function parseCreatedAt(str: string): Date {
  // str = "23-06-2025 04:21"
  const [datePart, timePart] = str.split(' ');              // ["23-06-2025","04:21"]
  const [day, month, year]   = datePart.split('-').map(Number);   // [23,6,2025]
  const [hours, minutes]     = timePart.split(':').map(Number);   // [4,21]
  return new Date(year, month - 1, day, hours, minutes);     // 월 −1, т.к. в JS месяцы 0-based
}

const urlRegex = /(https?:\/\/[^\s]+)/;

const SettlementsTable: React.FC<Props> = ({ filter }) => {
  const [page, setPage] = useState(1);
  const { data, error, isLoading } = useGetSettlementsQuery(undefined, { pollingInterval: 10000 });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentComment, setCurrentComment] = useState('');

  // Вызываем useMemo всегда, до любых ранних return
  const filtered = useMemo<TSettlementModel[]>(() => {
    if (!data) return [];
    if (!filter) return data;
    return data.filter(s => {
      if (filter.direction !== 'all' && s.direction !== filter.direction) return false;
      if (filter.status    !== 'all' && s.status    !== filter.status)    return false;
      if (filter.amountFrom !== ''  && s.amount < filter.amountFrom)     return false;
      if (filter.amountTo   !== ''  && s.amount > filter.amountTo)       return false;
      const dt: Date = parseCreatedAt(s.created_at);
      if (filter.dateFrom) {
          const from: Date = new Date(filter.dateFrom);
          from.setHours(0,  0,  0,   0);       // начало дня
          if (dt < from) return false;
      }
      if (filter.dateTo) {
          const to: Date = new Date(filter.dateTo);
          to.setHours(23, 59, 59, 999);       // конец дня
          if (dt > to) return false;
      }
      return true;
    });
  }, [data, filter]);

  if (isLoading) return <div>Загрузка заявок...</div>;
  if (error)     return <div>Ошибка загрузки заявок</div>;
  if (!data || data.length === 0) return <div className="no-data-icon">Нет заявок для отображения</div>;
  if (filtered.length === 0)      return <div className="no-data-icon">Нет заявок по выбранным фильтрам</div>;

  // пагинация
  const pageSize   = 7;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage   = Math.min(Math.max(page, 1), totalPages);
  const pageData   = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const openCommentModal = (comment: string) => {
    setCurrentComment(comment);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentComment('');
  };

  const tdArray: Td[][] = pageData.map(s => {
    const wallet = s.direction_display === 'Мерчант → Площадка'
      ? s.platform_wallet_address
      : s.merchant_wallet_address;

    return [
      {
        element: s.comment
          ? <span style={linkStyle} onClick={() => openCommentModal(s.comment!)}>
              {s.id}
            </span>
          : <span>{s.id}</span>,
        style: undefined,
      },
      { element: s.created_at,        style: undefined },
      { element: s.direction_display, style: undefined },
      { element: wallet,              style: undefined },
      { element: s.currency,          style: undefined },
      { element: s.amount,            style: undefined },
      { element: s.status_display,    style: undefined },
    ];
  });

  return (
    <div>
      <Table tableId='merchant-settlements' thArray={settlementsThArray} tdArray={tdArray} />

      <Pagination
        totalPages={totalPages}
        currentPage={safePage}
        setCurrentPage={setPage}
        nextPage={Math.min(safePage + 1, totalPages)}
        previousPage={Math.max(safePage - 1, 1)}
      />

      {createPortal(
        <ModalDefault
          title="Комментарий заявки"
          active={isModalOpen}
          setActive={setIsModalOpen}
        >
          <div style={{ maxWidth: 600, width: '100%', margin: '0 auto' }}>
            <div
              style={{
                background: '#f9fafb',
                padding: 16,
                borderRadius: 8,
                border: '1px solid #e5e7eb',
                overflowWrap: 'anywhere',
                wordBreak: 'break-all'
              }}
            >
              {urlRegex.test(currentComment) ? (
                <a
                  href={currentComment}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={linkStyle}
                >
                  {currentComment}
                </a>
              ) : (
                <p style={{ margin: 0, color: '#1f2937', whiteSpace: 'pre-wrap' }}>
                  {currentComment}
                </p>
              )}
            </div>
          </div>
        </ModalDefault>,
        document.body
      )}
    </div>
  );
};

export default SettlementsTable;
